import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Users,
  CreditCard,
  AlertTriangle,
  TrendingUp,
  Activity,
  Brain,
  Upload,
  Eye,
} from 'lucide-react';

interface Stats {
  accounts: {
    total: number;
    active: number;
    pastDue: number;
    canceled: number;
    incomplete: number;
    newLast30d: number;
    newLast7d: number;
  };
  usage: {
    totalImports: number;
    totalAnalyses: number;
  };
  activity: {
    billingEventsLast30d: number;
    auditEventsLast7d: number;
  };
}

interface UsageStats {
  activeSessions: number;
  loginsLast7d: number;
  imports: { last7d: number; last30d: number };
  analyses: { last7d: number; last30d: number };
  totalDataPoints: number;
}

interface RevenueStats {
  byPlan: { plan: string; count: number }[];
  byStatus: { status: string; count: number }[];
  recentCancellations: number;
}

export default function HealthDashboard() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [usage, setUsage] = useState<UsageStats | null>(null);
  const [revenue, setRevenue] = useState<RevenueStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      fetch('/api/admin/health/stats').then((r) => r.json()),
      fetch('/api/admin/health/analytics/usage').then((r) => r.json()),
      fetch('/api/admin/health/analytics/revenue').then((r) => r.json()),
    ])
      .then(([s, u, r]) => {
        setStats(s);
        setUsage(u);
        setRevenue(r);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error loading health dashboard:', err);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  const statCards = [
    {
      label: 'Total Accounts',
      value: stats?.accounts.total || 0,
      icon: Users,
      color: 'primary',
      href: '/health/accounts',
    },
    {
      label: 'Active Subscriptions',
      value: stats?.accounts.active || 0,
      icon: CreditCard,
      color: 'green',
      href: '/health/subscriptions',
    },
    {
      label: 'Past Due',
      value: stats?.accounts.pastDue || 0,
      icon: AlertTriangle,
      color: 'red',
      href: '/health/subscriptions?status=PAST_DUE',
    },
    {
      label: 'New (7d)',
      value: stats?.accounts.newLast7d || 0,
      icon: TrendingUp,
      color: 'blue',
      href: '/health/accounts',
    },
  ];

  const colorMap: Record<string, string> = {
    primary: 'bg-primary-500/10 text-primary-400',
    green: 'bg-green-500/10 text-green-400',
    red: 'bg-red-500/10 text-red-400',
    blue: 'bg-blue-500/10 text-blue-400',
    yellow: 'bg-yellow-500/10 text-yellow-400',
    purple: 'bg-purple-500/10 text-purple-400',
  };

  const statusColors: Record<string, string> = {
    ACTIVE: 'text-green-400 bg-green-400/10',
    PAST_DUE: 'text-red-400 bg-red-400/10',
    CANCELED: 'text-gray-400 bg-gray-400/10',
    INCOMPLETE: 'text-yellow-400 bg-yellow-400/10',
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white">Health SaaS Dashboard</h1>
          <p className="text-gray-400 mt-1">Oura Analytics platform overview</p>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((card) => (
          <Link
            key={card.label}
            to={card.href}
            className="bg-gray-800 rounded-xl p-6 hover:bg-gray-750 transition-colors"
          >
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-lg ${colorMap[card.color]}`}>
                <card.icon className="w-6 h-6" />
              </div>
              <div>
                <p className="text-sm text-gray-400">{card.label}</p>
                <p className="text-2xl font-bold text-white">{card.value}</p>
              </div>
            </div>
          </Link>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        {/* Subscription Breakdown */}
        <div className="bg-gray-800 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Subscription Status</h2>
          <div className="space-y-3">
            {revenue?.byStatus.map((item) => {
              const total = stats?.accounts.total || 1;
              const pct = Math.round((item.count / total) * 100);
              return (
                <div key={item.status} className="flex items-center gap-3">
                  <span
                    className={`px-2 py-1 rounded text-xs font-medium w-28 text-center ${
                      statusColors[item.status] || 'text-gray-400 bg-gray-400/10'
                    }`}
                  >
                    {item.status}
                  </span>
                  <div className="flex-1 bg-gray-700 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full ${
                        item.status === 'ACTIVE'
                          ? 'bg-green-500'
                          : item.status === 'PAST_DUE'
                          ? 'bg-red-500'
                          : item.status === 'CANCELED'
                          ? 'bg-gray-500'
                          : 'bg-yellow-500'
                      }`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <span className="text-sm text-gray-300 w-16 text-right">
                    {item.count} ({pct}%)
                  </span>
                </div>
              );
            })}
          </div>

          {/* Plan breakdown */}
          {revenue && revenue.byPlan.length > 0 && (
            <div className="mt-6 pt-4 border-t border-gray-700">
              <h3 className="text-sm font-medium text-gray-400 mb-3">Active by Plan</h3>
              <div className="flex gap-4">
                {revenue.byPlan.map((p) => (
                  <div key={p.plan} className="bg-gray-700/50 rounded-lg px-4 py-2">
                    <p className="text-xs text-gray-400 capitalize">{p.plan}</p>
                    <p className="text-lg font-bold text-white">{p.count}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {revenue && revenue.recentCancellations > 0 && (
            <div className="mt-4 p-3 bg-red-500/10 rounded-lg">
              <p className="text-sm text-red-400">
                {revenue.recentCancellations} cancellation{revenue.recentCancellations !== 1 ? 's' : ''} in last 30 days
              </p>
            </div>
          )}
        </div>

        {/* Engagement */}
        <div className="bg-gray-800 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Engagement</h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-700/50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-1">
                <Eye className="w-4 h-4 text-green-400" />
                <p className="text-xs text-gray-400">Active Sessions</p>
              </div>
              <p className="text-xl font-bold text-white">{usage?.activeSessions || 0}</p>
            </div>
            <div className="bg-gray-700/50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-1">
                <Activity className="w-4 h-4 text-blue-400" />
                <p className="text-xs text-gray-400">Logins (7d)</p>
              </div>
              <p className="text-xl font-bold text-white">{usage?.loginsLast7d || 0}</p>
            </div>
            <div className="bg-gray-700/50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-1">
                <Upload className="w-4 h-4 text-purple-400" />
                <p className="text-xs text-gray-400">Imports (30d)</p>
              </div>
              <p className="text-xl font-bold text-white">{usage?.imports.last30d || 0}</p>
            </div>
            <div className="bg-gray-700/50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-1">
                <Brain className="w-4 h-4 text-yellow-400" />
                <p className="text-xs text-gray-400">AI Analyses (30d)</p>
              </div>
              <p className="text-xl font-bold text-white">{usage?.analyses.last30d || 0}</p>
            </div>
          </div>

          <div className="mt-4 p-3 bg-gray-700/50 rounded-lg">
            <p className="text-sm text-gray-400">
              Total data points: <span className="text-white font-medium">{(usage?.totalDataPoints || 0).toLocaleString()}</span>
            </p>
          </div>
        </div>
      </div>

      {/* Growth Summary */}
      <div className="bg-gray-800 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-white mb-4">Growth Summary</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div>
            <p className="text-sm text-gray-400">30-Day Signups</p>
            <p className="text-2xl font-bold text-white">{stats?.accounts.newLast30d || 0}</p>
          </div>
          <div>
            <p className="text-sm text-gray-400">Incomplete</p>
            <p className="text-2xl font-bold text-yellow-400">{stats?.accounts.incomplete || 0}</p>
          </div>
          <div>
            <p className="text-sm text-gray-400">Billing Events (30d)</p>
            <p className="text-2xl font-bold text-white">{stats?.activity.billingEventsLast30d || 0}</p>
          </div>
          <div>
            <p className="text-sm text-gray-400">Audit Events (7d)</p>
            <p className="text-2xl font-bold text-white">{stats?.activity.auditEventsLast7d || 0}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
